import { Injectable } from '@angular/core';
import {
  CreateFileUploadFieldSettings,
  CreateFormFieldSettings
} from '@core/data-access/models/dynamic-form-field/create-form-field-settings.model';
import {
  BaseDynamicFormField,
  CheckboxField,
  DateInputField,
  DropdownField,
  EmailField,
  FieldKey,
  FieldType,
  FileUploadField,
  OptionKey,
  PasswordField,
  PhoneNumberField,
  RadioField,
  SubmitButton,
  TextInputField,
  ZipCodeField
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FileKind, FileType } from '@core/data-access/models/file-upload.model';
import {
  createTextInputOrDropdownField,
  defaultCreateFormFieldSettings
} from '@core/utils/dynamic-form-field/dynamic-form-field-helpers';
import { AppConstants } from '@shared/app-constants';
import { FieldSetting, NewsletterInputType } from '@store/customization/customization.interfaces';
import { SelectOption } from '@store/exhibition/exhibition.interface';
import { includes, join, values } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class DynamicFormFieldBuilder {
  createGenderField({
    fieldSetting,
    label,
    createFormFieldSettings = defaultCreateFormFieldSettings
  }: {
    fieldSetting: FieldSetting;
    label: string;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): DropdownField {
    const { order, isVisible, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.Gender,
      type: FieldType.Dropdown,
      label,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      options: [
        { key: 'male', value: 'gender.male', isDisabled: false },
        { key: 'female', value: 'gender.female', isDisabled: false },
        { key: 'diverse', value: 'gender.diverse', isDisabled: false },
        { key: 'notSpecified', value: 'gender.notSpecified', isDisabled: false }
      ]
    };
  }

  createTitleField({
    fieldSetting,
    label,
    placeholder,
    titles,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 50 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    titles: SelectOption[];
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField | DropdownField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    const baseDynamicFormField: BaseDynamicFormField = {
      key: FieldKey.Title,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns
    };

    return createTextInputOrDropdownField(baseDynamicFormField, titles, { maxLengthValidation });
  }

  createFirstNameField({
    fieldSetting,
    label,
    placeholder,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 50 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    return {
      key: FieldKey.FirstName,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      maxLengthValidation
    };
  }

  createLastNameField({
    fieldSetting,
    label,
    placeholder,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 50 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField {
    const { order, isMandatory } = fieldSetting;

    const { isHidden, isDisabled, isQuestionnaireField, columns, maxLengthValidation } = createFormFieldSettings;

    return {
      key: FieldKey.LastName,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      maxLengthValidation
    };
  }

  createCompanyField({
    fieldSetting,
    label,
    placeholder,
    isGoogleMapsApiEnabled,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 100 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    isGoogleMapsApiEnabled: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField | DropdownField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    const fieldTypeSettings = isGoogleMapsApiEnabled
      ? { type: FieldType.Dropdown, options: [] }
      : { type: FieldType.TextInput };

    return {
      key: FieldKey.Company,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      maxLengthValidation,
      ...fieldTypeSettings
    };
  }

  createPositionField({
    fieldSetting,
    label,
    placeholder,
    positions,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 100 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    positions: SelectOption[];
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField | DropdownField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    const baseDynamicFormField: BaseDynamicFormField = {
      key: FieldKey.Function,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns
    };

    return createTextInputOrDropdownField(baseDynamicFormField, positions, { maxLengthValidation });
  }

  createDepartmentField({
    fieldSetting,
    label,
    placeholder,
    departments,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 100 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    departments: SelectOption[];
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField | DropdownField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    const baseDynamicFormField: BaseDynamicFormField = {
      key: FieldKey.Department,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns
    };

    return createTextInputOrDropdownField(baseDynamicFormField, departments, { maxLengthValidation });
  }

  createOccupationalGroupField({
    fieldSetting,
    label,
    placeholder,
    occupationalGroups,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 100 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    occupationalGroups: SelectOption[];
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField | DropdownField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    const baseDynamicFormField: BaseDynamicFormField = {
      key: FieldKey.OccupationalGroup,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns
    };

    return createTextInputOrDropdownField(baseDynamicFormField, occupationalGroups, { maxLengthValidation });
  }

  createCountryField({
    fieldSetting,
    label,
    placeholder,
    createFormFieldSettings = defaultCreateFormFieldSettings
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): DropdownField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.Country,
      type: FieldType.Dropdown,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      options: []
    };
  }

  createStreetField({
    fieldSetting,
    label,
    placeholder,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 100 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    return {
      key: FieldKey.Street,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      maxLengthValidation
    };
  }

  createZipCodeField({
    fieldSetting,
    label,
    placeholder,
    isReadOnly,
    isCountryFieldVisible,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, zipCodeValidation: true, maxLengthValidation: 12 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    isReadOnly: boolean;
    isCountryFieldVisible: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): ZipCodeField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, zipCodeValidation, maxLengthValidation } =
      createFormFieldSettings;

    const fieldTypeSettings =
      isCountryFieldVisible && !isReadOnly ? { type: FieldType.Dropdown, options: [] } : { type: FieldType.TextInput };

    return {
      key: FieldKey.ZipCode,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      zipCodeValidation,
      maxLengthValidation,
      ...fieldTypeSettings
    };
  }

  createCityField({
    fieldSetting,
    label,
    placeholder,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 50 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    return {
      key: FieldKey.City,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      maxLengthValidation
    };
  }

  createPhoneNumberField({
    fieldSetting,
    label,
    placeholder,
    isTelephoneValidationDisabled,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    isTelephoneValidationDisabled: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): PhoneNumberField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.Telephone,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      phoneValidation: !isTelephoneValidationDisabled
    };
  }

  createVatNumberField({
    fieldSetting,
    label,
    placeholder,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, maxLengthValidation: 30 }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns, maxLengthValidation } =
      createFormFieldSettings;

    return {
      key: FieldKey.VatNumber,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      maxLengthValidation
    };
  }

  createEmailField({
    fieldSetting,
    label,
    placeholder,
    isEmailAndConfirmEmailMandatory = false,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      maxLengthValidation: 100,
      emailValidation: true,
      emailCaseInsensitiveValidation: true
    }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    maxLengthValidation: number;
    isEmailAndConfirmEmailMandatory: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): EmailField {
    const { isVisible, order, isMandatory } = fieldSetting;
    const {
      isHidden,
      isDisabled,
      isRequired,
      isQuestionnaireField,
      columns,
      maxLengthValidation,
      emailValidation,
      emailCaseInsensitiveValidation
    } = createFormFieldSettings;

    if (!isVisible && isHidden && !isEmailAndConfirmEmailMandatory) {
      return null;
    }

    return {
      key: FieldKey.Email,
      type: FieldType.Email,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired || isEmailAndConfirmEmailMandatory,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      maxLengthValidation,
      emailValidation,
      emailCaseInsensitiveValidation
    };
  }

  createConfirmEmailField({
    fieldSetting,
    label,
    placeholder,
    isEmailAndConfirmEmailMandatory = false,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      maxLengthValidation: 100,
      emailValidation: true,
      emailCaseInsensitiveValidation: true
    }
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    maxLengthValidation: string;
    isEmailAndConfirmEmailMandatory: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): EmailField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible && !isEmailAndConfirmEmailMandatory) {
      return null;
    }

    const {
      isHidden,
      isDisabled,
      isRequired,
      isQuestionnaireField,
      columns,
      maxLengthValidation,
      emailValidation,
      emailCaseInsensitiveValidation
    } = createFormFieldSettings;

    return {
      key: FieldKey.MailConfirm,
      type: FieldType.Email,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired || isEmailAndConfirmEmailMandatory,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      maxLengthValidation,
      emailValidation,
      emailCaseInsensitiveValidation
    };
  }

  createSearchAddressField({
    fieldSetting,
    label,
    placeholder,
    isGoogleMapsApiEnabled,
    isReadOnly,
    createFormFieldSettings = defaultCreateFormFieldSettings
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    isGoogleMapsApiEnabled: boolean;
    isReadOnly: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible || !isGoogleMapsApiEnabled || isReadOnly) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.Address,
      type: FieldType.Dropdown,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns
    };
  }

  createAddressField({
    fieldSetting,
    label,
    placeholder,
    isGoogleMapsLoaded,
    createFormFieldSettings = defaultCreateFormFieldSettings
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    isGoogleMapsLoaded: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): TextInputField {
    const { isVisible, order, isMandatory } = fieldSetting;

    if (!isVisible || !isGoogleMapsLoaded) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.Address,
      type: FieldType.TextInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns
    };
  }

  createSaveBillingAddressField({
    fieldSetting,
    checkboxHeading,
    checkboxOptionLabel,
    placeholder,
    isUserLoggedIn,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isRequired: false,
      columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }
    }
  }: {
    fieldSetting: FieldSetting;
    checkboxHeading: string;
    checkboxOptionLabel: string;
    placeholder: string;
    isUserLoggedIn: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): CheckboxField {
    const { order } = fieldSetting;

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.SaveAddress,
      type: FieldType.Checkbox,
      label: checkboxHeading,
      order,
      placeholder,
      isRequired,
      columns,
      isHidden: isHidden || !isUserLoggedIn,
      isDisabled,
      isQuestionnaireField,
      options: [
        {
          key: OptionKey.SaveBillingAddress,
          label: checkboxOptionLabel,
          value: false,
          isRequired: false,
          isDisabled: false
        }
      ]
    };
  }

  createDateOfBirthField({
    fieldSetting,
    label,
    placeholder,
    createFormFieldSettings = defaultCreateFormFieldSettings
  }: {
    fieldSetting: FieldSetting;
    label: string;
    placeholder: string;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): DateInputField {
    const { isVisible, order, isMandatory } = fieldSetting;
    if (!isVisible) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.DateOfBirth,
      type: FieldType.DateInput,
      label,
      placeholder,
      order,
      isRequired: isMandatory || isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns
    };
  }

  createPasswordField({
    label,
    placeholder,
    order,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isHidden: true,
      minLengthValidation: 6
    }
  }: {
    label: string;
    placeholder: string;
    order: number;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): PasswordField {
    const { isHidden, isDisabled, isQuestionnaireField, columns, minLengthValidation } = createFormFieldSettings;

    return {
      key: FieldKey.Password,
      type: FieldType.Password,
      label,
      placeholder,
      order,
      isRequired: true,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      minLengthValidation
    };
  }

  createVerifyPasswordField({
    label,
    placeholder,
    order,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isHidden: true,
      minLengthValidation: 6
    }
  }: {
    label: string;
    placeholder: string;
    order: number;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): PasswordField {
    const { isHidden, isDisabled, isQuestionnaireField, columns, minLengthValidation } = createFormFieldSettings;

    return {
      key: FieldKey.VerifyPassword,
      type: FieldType.Password,
      label,
      placeholder,
      order,
      isRequired: true,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      minLengthValidation
    };
  }

  createCreateAccountField({
    label,
    checkboxOptionLabel,
    order,
    showCreateAccountCheckbox,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 } }
  }: {
    label: string;
    checkboxOptionLabel: string;
    order: number;
    showCreateAccountCheckbox: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): CheckboxField {
    if (!showCreateAccountCheckbox) {
      return null;
    }

    const { isHidden, isDisabled, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.CreateAccount,
      type: FieldType.Checkbox,
      label,
      order,
      isRequired: false,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns,
      options: [
        {
          key: OptionKey.CreateAccount,
          label: checkboxOptionLabel,
          value: false,
          isRequired: false,
          isDisabled: false
        }
      ]
    };
  }

  createNewsletterField({
    checkboxHeading,
    checkboxOptionLabel,
    isCheckboxChecked = false,
    radioButtonHeading,
    radioButtonOptionLabels,
    order,
    newsletterInputType,
    createFormFieldSettings = { ...defaultCreateFormFieldSettings, columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 } }
  }: {
    newsletterInputType: NewsletterInputType;
    checkboxHeading: string;
    checkboxOptionLabel: string;
    isCheckboxChecked?: boolean;
    radioButtonHeading: string;
    radioButtonOptionLabels: {
      optIn: string;
      optOut: string;
    };
    order: number;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): CheckboxField | RadioField {
    if (newsletterInputType === NewsletterInputType.Hidden) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    const baseDynamicFormField: BaseDynamicFormField = {
      key: FieldKey.Newsletter,
      type: FieldType.Checkbox,
      label: '',
      order,
      isRequired,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      columns
    };

    const isCheckbox = includes(
      [NewsletterInputType.CheckboxCheckedByDefault, NewsletterInputType.CheckboxUncheckedByDefault],
      newsletterInputType
    );

    if (isCheckbox) {
      return {
        ...baseDynamicFormField,
        type: FieldType.Checkbox,
        label: checkboxHeading,
        isRequired: false,
        options: [
          {
            key: OptionKey.IsNewsletterChecked,
            label: checkboxOptionLabel,
            value: isCheckboxChecked || newsletterInputType === NewsletterInputType.CheckboxCheckedByDefault,
            isRequired: false
          }
        ]
      } as CheckboxField;
    } else if (newsletterInputType === NewsletterInputType.RadioButtonUnselected) {
      const { optIn, optOut } = radioButtonOptionLabels;

      return {
        ...baseDynamicFormField,
        type: FieldType.Radio,
        label: radioButtonHeading,
        isRequired: true,
        options: [
          {
            key: OptionKey.NewsletterOptIn,
            value: optIn
          },
          {
            key: OptionKey.NewsletterOptOut,
            value: optOut
          }
        ]
      } as RadioField;
    }
  }

  createCreateAccountButton({
    label,
    order,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isHidden: true,
      isRequired: false,
      columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }
    }
  }: {
    label: string;
    order: number;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): SubmitButton {
    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.CreateAccountButton,
      type: FieldType.SubmitButton,
      label,
      order,
      isRequired,
      columns,
      isHidden,
      isDisabled,
      isQuestionnaireField
    };
  }

  createFairCatalogueField({
    checkboxHeading,
    checkboxOptionLabel,
    order,
    showFairCatalogCheckbox,
    fairCatalogDefaultValue,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isRequired: false,
      columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }
    }
  }: {
    checkboxHeading: string;
    checkboxOptionLabel: string;
    order: number;
    showFairCatalogCheckbox: boolean;
    fairCatalogDefaultValue: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): CheckboxField {
    if (!showFairCatalogCheckbox) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.FairCatalogue,
      type: FieldType.Checkbox,
      label: checkboxHeading,
      order,
      isRequired,
      columns,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      options: [
        {
          key: OptionKey.IsFairCatalogChecked,
          label: checkboxOptionLabel,
          value: fairCatalogDefaultValue,
          isRequired: false,
          isDisabled: false
        }
      ]
    };
  }

  createDifferentBillingAddressField({
    checkboxHeading,
    checkboxOptionLabel,
    order,
    enableDifferentBillingAddress,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isRequired: false,
      columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }
    }
  }: {
    checkboxHeading: string;
    checkboxOptionLabel: string;
    order: number;
    enableDifferentBillingAddress: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): CheckboxField {
    if (!enableDifferentBillingAddress) {
      return null;
    }

    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.DifferentBillingAddress,
      type: FieldType.Checkbox,
      label: checkboxHeading,
      order,
      isRequired,
      columns,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      options: [
        {
          key: OptionKey.DifferentBillingAddress,
          label: checkboxOptionLabel,
          value: false,
          isRequired: false,
          isDisabled: false
        }
      ]
    };
  }

  creteSendToOwnerField({
    checkboxHeading,
    checkboxOptionLabel,
    order,
    isSendToOwnerChecked,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isRequired: false,
      columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }
    }
  }: {
    checkboxHeading: string;
    checkboxOptionLabel: string;
    order: number;
    isSendToOwnerChecked: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): CheckboxField {
    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.SendToOwner,
      type: FieldType.Checkbox,
      label: checkboxHeading,
      order,
      isRequired,
      columns,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      options: [
        {
          key: OptionKey.ToOwner,
          label: checkboxOptionLabel,
          value: isSendToOwnerChecked,
          isRequired: false,
          isDisabled: false
        }
      ]
    };
  }

  createGDPRField({
    checkboxHeading,
    checkboxOptionLabel,
    order,
    isGDPRConsentChecked,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isRequired: true,
      columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }
    }
  }: {
    checkboxHeading: string;
    checkboxOptionLabel: string;
    order: number;
    isGDPRConsentChecked: boolean;
    createFormFieldSettings?: CreateFormFieldSettings;
  }): CheckboxField {
    const { isHidden, isDisabled, isRequired, isQuestionnaireField, columns } = createFormFieldSettings;

    return {
      key: FieldKey.GDPR,
      type: FieldType.Checkbox,
      label: checkboxHeading,
      order,
      isRequired,
      columns,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      options: [
        {
          key: OptionKey.GDPRConsent,
          label: checkboxOptionLabel,
          value: isGDPRConsentChecked,
          isRequired: false,
          isDisabled: false
        }
      ]
    };
  }

  createFileUploadField({
    key,
    label,
    order,
    tooltip,
    createFormFieldSettings = {
      ...defaultCreateFormFieldSettings,
      isHidden: false,
      isRequired: false,
      columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
      fileKind: FileKind.File,
      acceptedFileTypes: values(FileType),
      maxFileSize: AppConstants.MAX_FILE_SIZE
    }
  }: {
    key: FieldKey;
    label: string;
    order: number;
    tooltip?: string;
    createFormFieldSettings: CreateFormFieldSettings & CreateFileUploadFieldSettings;
  }): FileUploadField {
    const {
      isHidden,
      isDisabled,
      isRequired,
      isQuestionnaireField,
      columns,
      fileKind,
      acceptedFileTypes,
      maxFileSize
    } = createFormFieldSettings;

    return {
      key,
      type: FieldType.FileUpload,
      label,
      tooltip,
      order,
      isRequired,
      columns,
      isHidden,
      isDisabled,
      isQuestionnaireField,
      fileKind,
      acceptedFileTypes: join(acceptedFileTypes, ', '),
      maxFileSize
    };
  }

  createDynamicFormFieldMapper() {
    return {
      Salutation: this.createGenderField,
      PersonTitle: this.createTitleField,
      FirstName: this.createFirstNameField,
      LastName: this.createLastNameField,
      Street: this.createStreetField,
      ZipCode: this.createZipCodeField,
      City: this.createCityField,
      Company: this.createCompanyField,
      PersonFunction: this.createPositionField,
      Phone: this.createPhoneNumberField,
      Country: this.createCountryField,
      Email: this.createEmailField,
      SearchAddress: this.createSearchAddressField,
      Address: this.createAddressField,
      SaveAddress: this.createSaveBillingAddressField,
      MailConfirm: this.createConfirmEmailField,
      VatNumber: this.createVatNumberField,
      Department: this.createDepartmentField,
      OccupationalGroup: this.createOccupationalGroupField,
      DateOfBirth: this.createDateOfBirthField
    };
  }
}
